import { next } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { GenieAnswerReadyDetail } from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';
import {
	ChatMessageType,
	ChatType,
	InputMethodType,
	PromptButtonVariant,
} from '@databox/types/domains/ai/chat.enums';
import { ContextObjectType } from '@databox/types/domains/ai/context.enums';

import type { RequestError, Service } from 'webapp-types';
import { GenieBuildBoardEntryPoint } from 'webapp/enums/ai-chat';
import { BillingFeature, BlockVisualization } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { CategoryInfo } from 'webapp/components/board/popup-select-category';
import BoardPopupSelectCategoryComponent from 'webapp/components/board/popup-select-category';

import type Block from 'webapp/models-custom/block';
import type Board from 'webapp/models-custom/board';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';

const SURPRISE_ME_KEY = 'surprise_me';

export interface BlockSlot {
	posX: number;
	posY: number;
	width: number;
	height: number;
}

interface PromptOptions {
	// Undefined for "Surprise me": Genie picks the category.
	categoryTitle?: string;
	isEmptyBoard: boolean;
	// Undefined for a new board: Genie picks how many.
	slots?: number;
	// The placeholder the user clicked; Genie puts the block there.
	slot?: BlockSlot;
}

export function buildBoardPrompt({
	categoryTitle,
	isEmptyBoard,
	slots,
	slot,
}: PromptOptions): string {
	const placement = slot
		? ` Place the new block at grid position x=${slot.posX}, y=${slot.posY} with width ${slot.width} and height ${slot.height}.`
		: '';

	if (!isEmptyBoard) {
		return `Add metrics to this dashboard now, without asking me questions first. Look at the metrics already on it and add up to ${slots} more that complement them, preferring my connected data sources, and show each in whatever visualization you think fits it best. Do not remove or change existing blocks. Then reply with a one-line summary of what you added. If nothing in my connected data sources fits, add nothing and suggest which data source to connect instead.${placement}`;
	}

	const category = categoryTitle
		? `Category: ${categoryTitle}.`
		: 'Pick the category yourself from what my connected data sources are best suited for.';

	const metrics =
		slots === undefined
			? 'pick the most important ones a team in that area looks at weekly, roughly 5 to 10 of them, and show each in whatever visualization you think fits it best'
			: `pick up to ${slots} of the most important ones a team in that area looks at weekly, and show each in whatever visualization you think fits it best`;

	return `Add metrics to this empty dashboard now, without asking me questions first. It already exists, so do not create a new dashboard. ${category} Prefer metrics from my connected data sources that fit the category; ${metrics}. Add them to this dashboard, then reply with a one-line summary of what you added. If none of my connected data sources fits the category, add nothing and suggest which data source to connect instead.${placement}`;
}

export default class FlowGenieBoardBuildService extends EmberService {
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('router') declare routerService: Service.Router;

	// While the new board is saving.
	@tracked isOpening = false;
	// The board Genie is filling and, from its first answer, the thread doing it. Every answer in
	// that thread reloads the board, even with the panel closed (its React root stays mounted).
	// The empty blocks Genie was asked to replace go once it has added a block.
	pendingBuild: {
		boardId: string;
		threadId: string | null;
		blockIds: (string | null)[];
		emptyBlockIds: (string | null)[];
	} | null = null;

	get isAvailable(): boolean {
		return this.aiChatService.isAiChatAvailable && this.navigationService.isMainApp;
	}

	// Without Genie on the plan or AI credits left, opens the upgrade or add-credits popup instead.
	checkCanBuild(): boolean {
		return (
			this.billingService.checkIfCan(BillingFeature.AI_CHAT_ASSISTANT, true) &&
			this.billingService.checkIfCan(BillingFeature.AI_CREDITS, true)
		);
	}

	openCategoryPopup(onSelect: (category: CategoryInfo) => void): void {
		this.popupService
			.createModal(BoardPopupSelectCategoryComponent, {
				popupClass: 'popup-select-category',
			})
			.open({ onSelect });
	}

	hasNoMetricBlocks(board: Board): boolean {
		return board.blockObjs.every(
			(block) =>
				this.isEmptyBlock(block) ||
				block.visualization === BlockVisualization.TEXT ||
				block.visualization === BlockVisualization.IMAGE,
		);
	}

	countEmptyGridPlaces(activeDatawallGrid: DatawallGrid | null): number {
		const nodes: { width: number; height: number }[] =
			activeDatawallGrid?.placeholdersGrid?.grid?.nodes || [];

		return nodes.filter((node) => node.width >= 2 && node.height >= 2).length;
	}

	async createBoardAndBuild(options: {
		color?: string;
		category: CategoryInfo;
		entryPoint: GenieBuildBoardEntryPoint;
	}): Promise<void> {
		if (this.isOpening) {
			return;
		}

		const { color, category, entryPoint } = options;

		this.isOpening = true;

		try {
			const boardRecord = await this.mmBoardsService
				.createNewModel({ name: category.title, color })
				.save();
			const board = this.mmBoardsService.findModel(boardRecord.id!)!;

			this.mmBoardsService.triggerDataboardCreated({ from: 'AI_databoards' }, board);
			this.navigationService.transitionToRoute('designer.board', board.id);

			await new Promise<void>((resolve) => next(() => resolve()));
			this.openBuild(board, { category, entryPoint });
		} catch (error) {
			this.requestService.showAllValidationErrors(error as RequestError);
		} finally {
			this.isOpening = false;
		}
	}

	completeBoard(
		board: Board,
		options: {
			category?: CategoryInfo;
			emptySlots: number;
			entryPoint: GenieBuildBoardEntryPoint;
			slot?: BlockSlot;
		},
	): void {
		if (this.isOpening) {
			return;
		}

		this.openBuild(board, options);
	}

	private openBuild(
		board: Board,
		options: {
			category?: CategoryInfo;
			emptySlots?: number;
			entryPoint: GenieBuildBoardEntryPoint;
			slot?: BlockSlot;
		},
	): void {
		const { category, emptySlots, entryPoint, slot } = options;
		const isEmptyBoard = this.hasNoMetricBlocks(board);
		// A single placeholder asks for one metric in its own slot; the buttons also hand Genie
		// the slots of the board's empty blocks, which Genie cannot fill in place.
		const emptyBlocks: Block[] =
			entryPoint === GenieBuildBoardEntryPoint.POPULATE_PLACEHOLDER
				? []
				: board.blockObjs.filter((block) => this.isEmptyBlock(block));

		this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
			origin: 'databoard',
			page: this.navigationService.activatedRoute,
			chat: ChatType.NEW,
			inputMethod: InputMethodType.PROMPT_BUTTON,
			inputMethodVariant: PromptButtonVariant.BUILD_DATABOARD,
			entryPoint,
		});

		this.aiChatService.openPanelWithContext({
			contextItems: [
				{
					objectType: ContextObjectType.DATABOARD,
					objectTypeId: board.numericId!,
				},
			],
			initialMessages: [
				{
					role: ChatMessageType.AI,
					content: `${isEmptyBoard ? 'Building' : 'Completing'} dashboard <b>${board.name}</b>`,
				},
				{
					role: ChatMessageType.HUMAN,
					content: buildBoardPrompt({
						categoryTitle:
							category?.key === SURPRISE_ME_KEY ? undefined : category?.title,
						isEmptyBoard,
						slots:
							emptySlots === undefined ? undefined : emptySlots + emptyBlocks.length,
						slot,
					}),
				},
			],
		});

		this.trackBuild(board, emptyBlocks);
	}

	// Data stories count as content even without metrics.
	private isEmptyBlock(block: Block): boolean {
		return block.isEmpty && !block.isDataStory;
	}

	private trackBuild(board: Board, emptyBlocks: Block[]): void {
		this.dropPendingBuild();
		this.pendingBuild = {
			boardId: board.id!,
			threadId: null,
			blockIds: board.blockObjs.map((block) => block.id),
			emptyBlockIds: emptyBlocks.map((block) => block.id),
		};
		this.eventManagerService.on(AppEvent.GENIE_ANSWER_READY, this, this.onAnswerReady);
		this.eventManagerService.on(
			AppEvent.GENIE_PANEL_CONVERSATION_ENDED,
			this,
			this.dropPendingBuild,
		);
	}

	private onAnswerReady(detail: GenieAnswerReadyDetail): void {
		const build = this.pendingBuild;

		if (!build) {
			return;
		}

		if (build.threadId === null) {
			if (this.aiChatService.threadId !== detail.threadId) {
				return;
			}

			build.threadId = detail.threadId;
		} else if (build.threadId !== detail.threadId) {
			return;
		}

		this.mmBoardsService.markBoardNotRequested(build.boardId);
		void this.mmBoardsService
			.requestBoardData(build.boardId)
			.then(() => this.removeReplacedEmptyBlocks(build));
	}

	// A block the user filled meanwhile is no longer empty and stays.
	private removeReplacedEmptyBlocks(build: NonNullable<typeof this.pendingBuild>): void {
		const blocks = this.mmBoardsService.findModel(build.boardId)?.blockObjs ?? [];

		if (!blocks.some((block) => !build.blockIds.includes(block.id))) {
			return;
		}

		const emptyBlocks = blocks.filter(
			(block) => build.emptyBlockIds.includes(block.id) && this.isEmptyBlock(block),
		);
		build.emptyBlockIds = [];

		emptyBlocks.forEach((block) => {
			block
				.delete()
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		});
	}

	// Genie may still add blocks, so the board is loaded fresh on its next visit; if it's on
	// screen, now.
	private dropPendingBuild(): void {
		const build = this.pendingBuild;

		if (!build) {
			return;
		}

		this.pendingBuild = null;
		this.eventManagerService.off(AppEvent.GENIE_ANSWER_READY, this);
		this.eventManagerService.off(AppEvent.GENIE_PANEL_CONVERSATION_ENDED, this);
		this.mmBoardsService.markBoardNotRequested(build.boardId);

		if (this.isBoardShown(build.boardId)) {
			void this.mmBoardsService.requestBoardData(build.boardId);
		}
	}

	private isBoardShown(boardId: string): boolean {
		const route = this.routerService.currentRoute?.find(
			(routeInfo) => routeInfo.name === 'designer.board',
		);

		return route?.params?.board_id === boardId;
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/genie-board-build': FlowGenieBoardBuildService;
	}
}
